import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SearchParams } from 'interfaces/program/programInterfaces';

import { ListProgram } from 'components/ListProgram/ListProgram';

import { useSearchPrograms } from 'hooks/programs/useSearchProgram';
import { useGetCompanies } from 'hooks/companies/useGetCompanies';
import { useGetServicePeriods } from 'hooks/periods/useGetServicePeriods';
import { Company, DataPeriod, ErrorData, IManagersHR } from 'interfaces/global/globalInterfaces';
import { BorderLinearProgress } from 'components/Loader/Loader';
import { ProgramsFilters } from 'components/MainFilters/ProgramsFilters';
import { ProgramsNavigation } from 'components/Navigation/ProgramsNavigation';
import { Box } from '@mui/system';
import { sessionStorageGetItem } from 'utils/sessionStorageGetItem';
import { isAccessAllowed } from 'utils/isAccessAllowed';
import { InfoText } from 'ui/other/Typography';
import { useGetCompanyAdmins } from 'hooks/permissions/useGetCompanyAdmins';
import { useGetAccessList } from 'hooks/permissions/useGetAccessList';
import { useGetDefaultPermissions } from 'hooks/permissions/useGetDefaultPermissions';
import { useSnackbar } from 'notistack';
import { mapProgramsToCityOptions, mapProgramsToSelectOptions } from 'utils/mapProgramsToSelectOptions';

interface IProgramsProps {
  handleManagersHR: (value: IManagersHR | null) => void;
}

export const Programs: React.FC<IProgramsProps> = ({ handleManagersHR }) => {
  // sessionStorage для главных фильтров
  const mainFilterCompanyPrograms = sessionStorageGetItem<Company>('mainFilterCompanyPrograms');
  const mainFilterPeriodPrograms = sessionStorageGetItem<DataPeriod>('mainFilterPeriodPrograms');
  // sessionStorage для фильтров таблицы
  const tableProgramsFilterProgram = sessionStorageGetItem<SearchParams>('tableProgramsFilterProgram');
  const tableProgramsFilterServices = sessionStorageGetItem<SearchParams[]>('tableProgramsFilterServices');
  const tableProgramsFilterCity = sessionStorageGetItem<SearchParams>('tableProgramsFilterCity');

  const [selectedCompanyByProgram, setSelectedCompanyByProgram] = useState<Company | null>(
    mainFilterCompanyPrograms || null
  );
  const [selectedPeriodByProgram, setSelectedPeriodByProgram] = useState<DataPeriod | null>(
    mainFilterPeriodPrograms || null
  );

  const [selectedPrograms, setSelectedPrograms] = useState<any>([]);

  const [isActionsWithProgramTooltip, setActionsWithProgramsTooltip] = useState(false);

  const [selectedProgramFilter, setSelectedProgramFilter] = useState<SearchParams | null>(
    tableProgramsFilterProgram || null
  );
  const [selectedServicesForProgramsFilter, setSelectedServicesForProgramsFilter] = useState<SearchParams[]>(
    tableProgramsFilterServices || []
  );
  const [selectedСityForProgram, setSelectedСityForProgram] = useState<SearchParams | null>(
    tableProgramsFilterCity || null
  );

  const prevCompanyUuidPrograms = useRef<string | undefined>(undefined);

  const resetProgramsTableFiltersAndPeriod = useCallback(() => {
    setSelectedProgramFilter(null);
    setSelectedServicesForProgramsFilter([]);
    setSelectedСityForProgram(null);
    setSelectedPeriodByProgram(null);
    sessionStorage.setItem('tableProgramsFilterProgram', JSON.stringify(''));
    sessionStorage.setItem('tableProgramsFilterServices', JSON.stringify(''));
    sessionStorage.setItem('tableProgramsFilterCity', JSON.stringify(''));
  }, []);

  const handleSelectedCompanyByProgram = useCallback(
    (company: Company | null) => {
      if (selectedCompanyByProgram?.uuid !== company?.uuid) {
        resetProgramsTableFiltersAndPeriod();
      }
      setSelectedCompanyByProgram(company);
    },
    [selectedCompanyByProgram?.uuid, resetProgramsTableFiltersAndPeriod]
  );

  useEffect(() => {
    const uuid = selectedCompanyByProgram?.uuid;
    if (
      prevCompanyUuidPrograms.current !== undefined &&
      prevCompanyUuidPrograms.current !== uuid
    ) {
      resetProgramsTableFiltersAndPeriod();
    }
    prevCompanyUuidPrograms.current = uuid;
  }, [selectedCompanyByProgram?.uuid, resetProgramsTableFiltersAndPeriod]);

  const token = localStorage.getItem('token');

  const { enqueueSnackbar } = useSnackbar();

  const { accessList } = useGetAccessList({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { defaultPermissions } = useGetDefaultPermissions({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { isLoadingCompanies, companiesSearch: allCompanies } = useGetCompanies();

  useEffect(() => {
    if (!allCompanies.length) {
      return;
    }

    const storedCompany = sessionStorageGetItem<Company>('mainFilterCompanyPrograms');
    if (!storedCompany?.uuid) {
      setSelectedCompanyByProgram(allCompanies[0]);
      sessionStorage.setItem('mainFilterCompanyPrograms', JSON.stringify(allCompanies[0]));
      return;
    }

    const matchedCompany = allCompanies.find((company) => company.uuid === storedCompany.uuid) ?? null;
    if (matchedCompany) {
      setSelectedCompanyByProgram(matchedCompany);
      sessionStorage.setItem('mainFilterCompanyPrograms', JSON.stringify(matchedCompany));
    }
  }, [allCompanies]);

  const { isLoadingServicePeriods, dataServicePeriods } = useGetServicePeriods({
    companies: selectedCompanyByProgram ? [selectedCompanyByProgram] : [],
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const dataPeriods = useMemo(
    () => (dataServicePeriods ? [...dataServicePeriods].reverse() : []),
    [dataServicePeriods]
  );

  useEffect(() => {
    if (!selectedCompanyByProgram) {
      setSelectedPeriodByProgram(null);
      sessionStorage.setItem('mainFilterPeriodPrograms', JSON.stringify(''));
      return;
    }

    if (!dataServicePeriods) {
      return;
    }

    const storedPeriod = sessionStorageGetItem<DataPeriod>('mainFilterPeriodPrograms');
    const activePeriodExist = dataPeriods.some((item) => item.activePeriod);

    dataPeriods.forEach((period: DataPeriod) => {
      if (storedPeriod) {
        if (storedPeriod.uuid === period.uuid) {
          setSelectedPeriodByProgram(period);
          sessionStorage.setItem('mainFilterPeriodPrograms', JSON.stringify(period));
          handleManagersHR({
            accountEmail: period.accountEmail,
            accountPhone: period.accountPhone,
            curatorEmail: period.curatorEmail,
            curatorPhone: period.curatorPhone,
            accountFullName: period.accountFullName,
            doctorFullName: period.doctorFullName,
            doctorEmail: period.doctorEmail,
          });
        }
      } else if (period.activePeriod) {
        setSelectedPeriodByProgram(period);
        sessionStorage.setItem('mainFilterPeriodPrograms', JSON.stringify(period));

        handleManagersHR({
          accountEmail: period.accountEmail,
          accountPhone: period.accountPhone,
          curatorEmail: period.curatorEmail,
          curatorPhone: period.curatorPhone,
          accountFullName: period.accountFullName,
          doctorFullName: period.doctorFullName,
        });
      } else if (!activePeriodExist) {
        setSelectedPeriodByProgram(dataPeriods[0]);
        sessionStorage.setItem('mainFilterPeriodPrograms', JSON.stringify(dataPeriods[0]));

        handleManagersHR({
          accountEmail: dataPeriods[0].accountEmail,
          accountPhone: dataPeriods[0].accountPhone,
          curatorEmail: dataPeriods[0].curatorEmail,
          curatorPhone: dataPeriods[0].curatorPhone,
          accountFullName: dataPeriods[0].accountFullName,
          doctorFullName: dataPeriods[0].doctorFullName,
        });
      }
    });
  }, [dataPeriods, dataServicePeriods, handleManagersHR, selectedCompanyByProgram]);

  const {
    isLoadingSearchPrograms,
    programs: programsData,
    pageParams,
  } = useSearchPrograms({
    isProgramsAllowed: isAccessAllowed(mainFilterCompanyPrograms?.uuid, 'HR_programs', accessList, defaultPermissions),
    company: selectedCompanyByProgram,
    period: selectedPeriodByProgram,
    selectedProgram: selectedProgramFilter,
    selectedServices: selectedServicesForProgramsFilter,
    selectedСity: selectedСityForProgram,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const programsQuantity = Number(pageParams?.total) || 0;
  const searchByProgrammArr = useMemo(() => mapProgramsToSelectOptions(programsData), [programsData]);
  const searchByCityArr = useMemo(() => mapProgramsToCityOptions(programsData), [programsData]);

  const { companyAdminsData } = useGetCompanyAdmins({
    companyUuid: selectedCompanyByProgram?.uuid,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  return (
    <>
      {isLoadingCompanies || isLoadingSearchPrograms || (isLoadingServicePeriods && <BorderLinearProgress />)}

      <ProgramsNavigation
        selectedPrograms={selectedPrograms}
        programsData={programsData}
        isActionsWithProgramTooltip={isActionsWithProgramTooltip}
        handleActionsWithProgramsTooltip={setActionsWithProgramsTooltip}
        accessList={accessList}
        defaultPermissions={defaultPermissions}
      />

      <ProgramsFilters
        allCompanies={allCompanies}
        selectedCompanyByProgram={selectedCompanyByProgram}
        handleSelectedCompanyByProgram={handleSelectedCompanyByProgram}
        dataPeriods={dataPeriods}
        selectedPeriodByProgram={selectedPeriodByProgram}
        handleSelectedPeriodByProgram={setSelectedPeriodByProgram}
      />
      {isAccessAllowed(mainFilterCompanyPrograms?.uuid, 'HR_programs', accessList, defaultPermissions) ? (
        <Box component="section" sx={{ marginTop: '32px' }}>
          <ListProgram
            programsQuantity={programsQuantity}
            programsData={programsData}
            searchByProgrammArr={searchByProgrammArr}
            searchByCityArr={searchByCityArr}
            selectedProgramFilter={selectedProgramFilter}
            handleSelectedProgramFilter={setSelectedProgramFilter}
            selectedServicesForProgramsFilter={selectedServicesForProgramsFilter}
            handleSelectedServices={setSelectedServicesForProgramsFilter}
            selectedСityForProgram={selectedСityForProgram}
            handleSelectedСity={setSelectedСityForProgram}
            selectedPrograms={selectedPrograms}
            handleSelectedPrograms={setSelectedPrograms}
            handleActionsWithProgramsTooltip={setActionsWithProgramsTooltip}
          />
        </Box>
      ) : (
        <InfoText variant="h5">
          Для просмотра данного раздела у вас недостаточно прав, обратитесь к администратору{' '}
          {companyAdminsData
            ?.slice(0, 3)
            ?.map((item) => item?.user?.email)
            ?.join(',')}
          , если вам необходимы данные для работы
        </InfoText>
      )}
    </>
  );
};
